import { supabase } from './supabaseClient';

/**
 * Forum Service - Handles all forum post operations with Supabase
 * Optimized with caching and performance improvements
 */

// Simple in-memory cache with localStorage backup
let postsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30000; // 30 seconds
const LOCALSTORAGE_KEY = 'alera_forum_posts_cache';

// Initialize cache from localStorage on module load
try {
  const stored = localStorage.getItem(LOCALSTORAGE_KEY);
  if (stored) {
    const { data, timestamp } = JSON.parse(stored);
    const now = Date.now();
    if (now - timestamp < CACHE_DURATION) {
      postsCache = data;
      cacheTimestamp = timestamp;
    }
  }
} catch (e) {
  console.warn('Failed to load cached posts from localStorage');
}

// Get forum posts with pagination and caching
export const getForumPosts = async (options = {}) => {
  const {
    forceRefresh = false,
    limit = 20,
    offset = 0,
    category = null
  } = options;

  try {
    // Return cached data immediately if available and fresh (only for first page)
    const now = Date.now();
    if (!forceRefresh && offset === 0 && !category && postsCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
      return { success: true, data: postsCache, fromCache: true };
    }

    // Build query
    let query = supabase
      .from('forum_posts')
      .select('id, content, category, author, avatar, user_id, user_email, likes, replies, image_url, pinned, created_at', { count: 'exact' })
      .order('pinned', { ascending: false })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    // Add category filter if specified
    if (category) {
      query = query.eq('category', category);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching forum posts:', error);
      // Return cached data if available, even if stale
      if (postsCache && offset === 0) {
        return { success: true, data: postsCache, fromCache: true, stale: true };
      }
      return { success: false, error: error.message, data: [] };
    }

    // Update cache only for first page
    if (offset === 0 && !category) {
      postsCache = data || [];
      cacheTimestamp = now;
      
      try {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify({
          data: postsCache,
          timestamp: cacheTimestamp
        }));
      } catch (e) {
        console.warn('Failed to cache posts in localStorage');
      }
    }

    return { 
      success: true, 
      data: data || [], 
      count: count || 0,
      hasMore: count ? (offset + limit) < count : false
    };
  } catch (error) {
    console.error('Error in getForumPosts:', error);
    // Return cached data if available
    if (postsCache && offset === 0) {
      return { success: true, data: postsCache, fromCache: true, stale: true };
    }
    return { success: false, error: error.message, data: [] };
  }
};

// Clear cache (useful after creating/deleting posts)
export const clearPostsCache = () => {
  postsCache = null;
  cacheTimestamp = null;
  try {
    localStorage.removeItem(LOCALSTORAGE_KEY);
  } catch (e) {
    console.warn('Failed to clear localStorage cache');
  }
};

// Create a new forum post
export const createForumPost = async (postData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    const newPost = {
      content: postData.content,
      category: postData.category,
      author: postData.author || 'Anonymous',
      avatar: postData.avatar || '👤',
      user_id: user?.id || null,
      user_email: user?.email || null,
      likes: 0,
      replies: 0,
      image_url: postData.image || null,
      pinned: false
    };

    const { data, error } = await supabase
      .from('forum_posts')
      .insert([newPost])
      .select()
      .single();

    if (error) {
      console.error('Error creating forum post:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in createForumPost:', error);
    return { success: false, error: error.message };
  }
};

// Update post likes
export const updatePostLikes = async (postId, newLikesCount) => {
  try {
    const { data, error } = await supabase
      .from('forum_posts')
      .update({ likes: newLikesCount })
      .eq('id', postId)
      .select()
      .single();

    if (error) {
      console.error('Error updating post likes:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in updatePostLikes:', error);
    return { success: false, error: error.message };
  }
};

// Delete a post and its image from storage
export const deleteForumPost = async (postId) => {
  try {
    // First, get the post to find the image URL
    const { data: post, error: fetchError } = await supabase
      .from('forum_posts')
      .select('image_url')
      .eq('id', postId)
      .single();

    if (fetchError) {
      console.error('Error fetching post for deletion:', fetchError);
      return { success: false, error: fetchError.message };
    }

    // Delete the image from storage if it exists
    if (post?.image_url && post.image_url.includes('supabase.co/storage')) {
      try {
        // Extract file path from URL
        const urlParts = post.image_url.split('/forum-images/');
        if (urlParts.length > 1) {
          const filePath = urlParts[1];
          const { error: storageError } = await supabase.storage
            .from('forum-images')
            .remove([filePath]);
          
          if (storageError) {
            console.warn('Error deleting image from storage:', storageError);
            // Continue with post deletion even if image delete fails
          }
        }
      } catch (storageError) {
        console.warn('Failed to delete image from storage:', storageError);
        // Continue with post deletion
      }
    }

    // Delete the post from database
    const { error } = await supabase
      .from('forum_posts')
      .delete()
      .eq('id', postId);

    if (error) {
      console.error('Error deleting forum post:', error);
      return { success: false, error: error.message };
    }

    // Clear cache after deletion
    clearPostsCache();

    return { success: true };
  } catch (error) {
    console.error('Error in deleteForumPost:', error);
    return { success: false, error: error.message };
  }
};

// Subscribe to real-time post updates (optimized)
export const subscribeToForumPosts = (callback) => {
  const subscription = supabase
    .channel('forum_posts_changes', {
      config: {
        broadcast: { self: false },
        presence: { key: '' },
      },
    })
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'forum_posts'
      },
      (payload) => {
        callback(payload);
        // Clear cache when posts change
        clearPostsCache();
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Real-time subscription active');
      }
    });

  return subscription;
};

// =====================================================
// COMMENT FUNCTIONS
// =====================================================

// Get comments for a specific post
export const getPostComments = async (postId) => {
  try {
    const { data, error } = await supabase
      .from('forum_comments')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching comments:', error);
      return { success: false, error: error.message, data: [] };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error in getPostComments:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Create a new comment
export const createComment = async (postId, content) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Get user profile for name and avatar
    const userName = localStorage.getItem('userName') || user.email?.split('@')[0] || 'Member';
    const avatarUrl = localStorage.getItem('memberAvatarUrl') || null;

    const newComment = {
      post_id: postId,
      user_id: user.id,
      user_email: user.email,
      user_name: userName,
      avatar_url: avatarUrl,
      content: content.trim()
    };

    const { data, error } = await supabase
      .from('forum_comments')
      .insert([newComment])
      .select()
      .single();

    if (error) {
      console.error('Error creating comment:', error);
      return { success: false, error: error.message };
    }

    // Clear posts cache since reply count changed
    clearPostsCache();

    return { success: true, data };
  } catch (error) {
    console.error('Error in createComment:', error);
    return { success: false, error: error.message };
  }
};

// Delete a comment
export const deleteComment = async (commentId) => {
  try {
    const { error } = await supabase
      .from('forum_comments')
      .delete()
      .eq('id', commentId);

    if (error) {
      console.error('Error deleting comment:', error);
      return { success: false, error: error.message };
    }

    // Clear posts cache since reply count changed
    clearPostsCache();

    return { success: true };
  } catch (error) {
    console.error('Error in deleteComment:', error);
    return { success: false, error: error.message };
  }
};

// Update a comment
export const updateComment = async (commentId, content) => {
  try {
    const { data, error } = await supabase
      .from('forum_comments')
      .update({ 
        content: content.trim(),
        updated_at: new Date().toISOString()
      })
      .eq('id', commentId)
      .select()
      .single();

    if (error) {
      console.error('Error updating comment:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in updateComment:', error);
    return { success: false, error: error.message };
  }
};
